"use client"

import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  celebrationCtaPropType,
  premiumCelebrationDefaultProps,
  premiumCelebrationPropTypes,
} from './premium-celebration.types'

function useWindowSize(isActive) {
  const [size, setSize] = useState({ width: 0, height: 0 })

  useEffect(() => {
    if (!isActive || typeof window === 'undefined') {
      return
    }

    function update() {
      setSize({ width: window.innerWidth, height: window.innerHeight })
    }

    update()

    window.addEventListener('resize', update)

    return () => {
      window.removeEventListener('resize', update)
    }
  }, [isActive])

  return size
}

export function PremiumCelebration({
  isVisible,
  message,
  subcopy,
  cta,
  onDismiss,
  className,
  confettiProps,
  variant = 'overlay',
  autoDismissDelay,
  ...rest
}) {
  const t = useTranslations('oracle.premiumCelebration')
  const [ConfettiComponent, setConfettiComponent] = useState(null)
  const [hasLoadedConfetti, setHasLoadedConfetti] = useState(false)
  const containerRef = useRef(null)
  const ctaRef = useRef(null)
  const dismissRef = useRef(null)
  const { width, height } = useWindowSize(isVisible && variant === 'overlay')

  useEffect(() => {
    if (!isVisible) {
      return undefined
    }

    let isMounted = true

    import('react-confetti')
      .then((module) => {
        if (!isMounted) {
          return
        }

        setConfettiComponent(() => module.default || module.Confetti || null)
        setHasLoadedConfetti(true)
      })
      .catch(() => {
        setHasLoadedConfetti(false)
      })

    return () => {
      isMounted = false
    }
  }, [isVisible])

  useEffect(() => {
    if (!isVisible) {
      return undefined
    }

    const focusTarget = ctaRef.current || dismissRef.current

    const timer = window.requestAnimationFrame(() => {
      if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true })
      }
    })

    return () => {
      window.cancelAnimationFrame(timer)
    }
  }, [isVisible])

  useEffect(() => {
    if (!isVisible || !autoDismissDelay || typeof autoDismissDelay !== 'number') {
      return undefined
    }

    const timeout = window.setTimeout(() => {
      if (onDismiss) {
        onDismiss()
      }
    }, autoDismissDelay)

    return () => {
      window.clearTimeout(timeout)
    }
  }, [autoDismissDelay, isVisible, onDismiss])

  const handleDismiss = useCallback(() => {
    if (onDismiss) {
      onDismiss()
    }
  }, [onDismiss])

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        handleDismiss()
      }
    },
    [handleDismiss],
  )

  const confettiDimensions = useMemo(() => {
    if (variant === 'inline' && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()

      return { width: rect.width, height: rect.height + 120 }
    }

    const fallbackWidth = typeof window !== 'undefined' ? window.innerWidth : 0
    const fallbackHeight = typeof window !== 'undefined' ? window.innerHeight : 0

    return { width: width || fallbackWidth, height: height || fallbackHeight }
  }, [height, variant, width])

  const renderConfetti = ConfettiComponent && isVisible

  const content = (
    <motion.div
      ref={containerRef}
      key="premium-celebration"
      initial={{ opacity: 0, y: 32, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 32, scale: 0.95 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      role="status"
      aria-live="assertive"
      className={clsx(
        'relative flex w-full max-w-xl flex-col gap-4 rounded-3xl border border-[#D4B6FF]/40 bg-linear-to-b from-[#1A132B]/95 via-[#24124F]/90 to-[#100424]/95 p-6 text-[#FBFBFE] shadow-2xl',
        className,
      )}
      data-testid="premium-celebration"
      {...rest}
    >
      <div className="absolute inset-0 rounded-3xl bg-linear-to-br from-[#A45DE8]/10 via-transparent to-[#D4B6FF]/10" aria-hidden="true" />
      <div className="relative space-y-2">
        <p className="text-xs uppercase tracking-[0.35em] text-[#D4B6FF]/80">{t('tagline')}</p>
        <h3 className="font-marcellus text-2xl leading-tight">{message}</h3>
        {subcopy ? <p className="text-sm text-[#D4B6FF]/80">{subcopy}</p> : null}
      </div>

      <div className="relative flex flex-wrap gap-3 pt-2">
        {cta ? (
          <button
            type="button"
            ref={ctaRef}
          className="rounded-full bg-linear-to-r from-[#A45DE8] via-[#C279FF] to-[#6A3683] px-5 py-2 text-sm font-semibold text-[#FBFBFE] shadow-lg shadow-[#A45DE8]/40 transition hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[#D4B6FF]/60 focus:ring-offset-2 focus:ring-offset-[#050316]"
            onClick={() => {
              cta.onClick()
            }}
          >
            {cta.label}
          </button>
        ) : null}

        <button
          type="button"
          ref={dismissRef}
          className="rounded-full border border-[#D4B6FF]/30 px-4 py-2 text-sm font-medium text-[#D4B6FF] transition hover:border-[#D4B6FF] hover:text-[#FBFBFE] focus:outline-none focus:ring-2 focus:ring-[#D4B6FF]/60 focus:ring-offset-2 focus:ring-offset-[#050316]"
          onClick={handleDismiss}
        >
          {t('dismiss')}
        </button>
      </div>

      {renderConfetti && hasLoadedConfetti ? (
        <ConfettiComponent
          width={confettiDimensions.width}
          height={confettiDimensions.height}
          numberOfPieces={200}
          recycle={false}
          gravity={0.25}
          tweenDuration={6000}
          {...confettiProps}
        />
      ) : null}
    </motion.div>
  )

  if (variant === 'inline') {
    return (
      <AnimatePresence>
        {isVisible ? (
          <div onKeyDown={handleKeyDown} className="relative w-full overflow-hidden">
            {content}
          </div>
        ) : null}
      </AnimatePresence>
    )
  }

  return (
    <AnimatePresence>
      {isVisible ? (
        <motion.div
          key="celebration-overlay"
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#050316]/85 px-4 py-12 backdrop-blur"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onKeyDown={handleKeyDown}
        >
          {content}
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

PremiumCelebration.propTypes = {
  ...premiumCelebrationPropTypes,
  cta: PropTypes.oneOfType([celebrationCtaPropType, PropTypes.oneOf([null])]),
}

PremiumCelebration.defaultProps = {
  ...premiumCelebrationDefaultProps,
}

export default PremiumCelebration
